import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { ReviewListModel, ReviewModel, CreateReviewModel, UpdateReviewModel } from "../types/review.types";
import type { ODataListResponse } from "../types/odata.types";
import { type ListQueryParams, toQueryString } from "./querying/listQueryParams";
import { http } from "./httpClient";

export const getReviewsInfiniteQuery = (params: Omit<ListQueryParams, 'skip'> = {}) => infiniteQueryOptions({
    queryKey: ['reviews', 'list', 'infinite', params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
        const qs = toQueryString({ ...params, skip: pageParam });
        return await http.get<ODataListResponse<ReviewListModel>>(qs ? `/reviews?${qs}` : '/reviews');
    },
    getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((sum, page) => sum + page.value.length, 0);
        return loaded < lastPage['@odata.count'] ? loaded : undefined;
    },
    staleTime: 20 * 1000
});

export const getReviewByIdQuery = (reviewId: string) => queryOptions({
    queryKey: ['reviews', 'single', reviewId],
    queryFn: async () => {
        return await http.get<ReviewModel>(`/reviews/${reviewId}`);
    },
    enabled: reviewId !== undefined,
    staleTime: 20 * 1000
});

export const createReview = async (input: CreateReviewModel): Promise<string> => {
    return await http.post<string, CreateReviewModel>('/reviews', input);
};

export const updateReview = async (reviewId: string, input: UpdateReviewModel): Promise<void> => {
    await http.put<string, UpdateReviewModel>(`/reviews/${reviewId}`, input);
};
